import { Component, DoCheck, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { LoginService } from '@intranet/modules/login/login.service';
import { AuthTokenService } from '@intranet/core/services/authtoken.service';
import { DesktopAppService } from '@intranet/core/services/desktop-app.service';
import { UserGroup } from '@intranet/modules/users/user.types';

@Component({
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss'],
})
export class LoginFormComponent implements OnInit, DoCheck {
    userGroups: UserGroup[];
    error: any;

    /** Set once we've redirected away from /login post-login in the timer app —
     *  prevents repeat navigations from later change-detection cycles. */
    private hasRedirectedToDesktop = false;

    constructor(
        public loginService: LoginService,
        public router: Router,
        public authTokenService: AuthTokenService,
        private desktopApp: DesktopAppService,
    ) {}

    ngOnInit(): void {
        this.loginService.getUserGroups().subscribe(
            (r) => {
                if (r.success) {
                    this.userGroups = r.userGroups;
                } else {
                    this.error = r.error;
                }
            },
            (error) => (this.error = error)
        );
    }

    /** Belt-and-braces redirect for the Visarc Timer app: once login completes
     *  (loginState becomes 'loginmatched'), bounce to /desktop. Covers cases
     *  where MSAL completes auth without triggering a fresh AppComponent bootstrap
     *  pass — the user would otherwise be stuck on /login showing "You are
     *  logged in as ...". Gated on isTimerApp + a single-shot flag so this is a
     *  complete no-op for the regular browser site. */
    ngDoCheck(): void {
        if (this.hasRedirectedToDesktop) return;
        if (!this.desktopApp.isTimerApp) return;
        if (this.loginService.loginState !== 'loginmatched') return;
        this.hasRedirectedToDesktop = true;
        this.router.navigateByUrl('/desktop');
    }

    lastName: string;
    firstName: string;
    userGroup: UserGroup | undefined;
    associateState: 'withexisting' | 'withnew' | '' = '';

    private username: string;
    private password: string;
    rememberMe = true;

    get loggedInName(): string | null {
        return this.loginService.username;
    }

    get isLoggedIn(): boolean {
        return this.loginService.isLoggedIn;
    }

    onSubmit() {
        if (this.associateState == 'withexisting') {
            console.log('sign in with existing');
            this.loginService
                .matchExternalLogin(this.username, this.password)
                .subscribe((r) => {
                    if (r.success) {
                        if (this.loginService.loginState == 'loginmatched') {
                            if (this.loginService.redirectUrl) {
                                let url = this.loginService.redirectUrl;
                                this.loginService.redirectUrl = null;
                                this.router.navigate([url]);
                            } else {
                                this.router.navigate(['/home']);
                            }
                        }
                    } else {
                        this.error = r.error;
                    }
                });
        } else if (this.associateState == 'withnew') {
            console.log('sign in with new account');
            if (this.userGroup) {
                this.loginService
                    .createNewAccountForExternal(
                        this.firstName,
                        this.lastName,
                        this.loginService.externalEmail,
                        this.loginService.externalId
                    )
                    .subscribe(
                        (r) => {
                            if (r.success) {
                                if (
                                    this.loginService.loginState ==
                                    'loginmatched'
                                ) {
                                    if (this.loginService.redirectUrl) {
                                        let url = this.loginService.redirectUrl;
                                        this.loginService.redirectUrl = null;
                                        this.router.navigate([url]);
                                    } else {
                                        this.router.navigate(['/home']);
                                    }
                                }
                            } else {
                                this.error = r.error;
                            }
                        },
                        (error) => (this.error = error)
                    );
            }
        }
    }

    logout() {
        this.loginService.logoutAzure();
    }

    popupLogin() {
        this.loginService.popupLogin().subscribe(
            (result) => {
                console.log(result);
            },
            (error) => {
                console.log('error: ' + error);
            }
        );
    }

    redirectLogin() {
        this.loginService.redirectLogin();
    }

    silentLogin() {
        this.loginService.silentLogin().subscribe(
            (token) => {
                console.log(token);
            },
            (error) => {
                console.log('Error: ' + error);
            }
        );
    }

    withoutLogin() {
        this.loginService.withoutLogin();
    }

    chooseExisting() {
        this.associateState = 'withexisting';
    }

    chooseNew() {
        this.associateState = 'withnew';
        var display = this.loginService.getLoggedInAdUserDisplayName();
        var split = display.split(' ').filter((s) => s != '');
        if (split.length > 0) {
            this.firstName = split[0];
        }
        if (split.length > 1) {
            this.lastName = split.slice(1).join(' ');
        }
    }

    cancelAssociate() {
        this.associateState = '';
    }
}
